'use client';

import {
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Mail,
  MapPin,
  Pencil,
  Phone,
  RefreshCw,
  Sparkles,
  User,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

// Types for the sync suggestion system
interface FieldChange {
  id: string;
  fieldName: string;
  fieldLabel: string;
  currentValue: string | null;
  newValue: string | null;
  selected: boolean;
}

interface SyncSuggestion {
  id: string;
  entityName: string;
  entityType: 'provider' | 'organization' | 'contact';
  direction: 'push' | 'pull';
  matchConfidence: number;
  aiReasoning: string;
  timestamp: Date;
  changes: FieldChange[];
  status: 'pending' | 'accepted' | 'rejected';
}

// Mock data for demonstration
const mockSuggestions: SyncSuggestion[] = [
  {
    id: '1',
    entityName: 'Dr. Sarah Smith',
    entityType: 'provider',
    direction: 'push',
    matchConfidence: 98,
    aiReasoning:
      'Finch has newer contact information. Phone number was updated 3 days ago, fax number added yesterday.',
    timestamp: new Date(Date.now() - 2 * 60 * 1000),
    changes: [
      {
        id: '1a',
        fieldName: 'phone',
        fieldLabel: 'Phone',
        currentValue: '(555) 123-4567',
        newValue: '(555) 987-6543',
        selected: true,
      },
      {
        id: '1b',
        fieldName: 'fax',
        fieldLabel: 'Fax',
        currentValue: null,
        newValue: '(555) 987-6500',
        selected: true,
      },
    ],
    status: 'pending',
  },
  {
    id: '2',
    entityName: 'Wellstar Cobb Medical Center',
    entityType: 'organization',
    direction: 'pull',
    matchConfidence: 94,
    aiReasoning:
      'Filevine has updated billing address and new department contact. Recommend pulling to keep Finch records current.',
    timestamp: new Date(Date.now() - 15 * 60 * 1000),
    changes: [
      {
        id: '2a',
        fieldName: 'billing_address',
        fieldLabel: 'Billing Address',
        currentValue: '123 Medical Dr, Suite 100',
        newValue: '456 Healthcare Blvd, Floor 3',
        selected: true,
      },
      {
        id: '2b',
        fieldName: 'department_phone',
        fieldLabel: 'Department Phone',
        currentValue: '(404) 555-0100',
        newValue: '(404) 555-0200',
        selected: true,
      },
      {
        id: '2c',
        fieldName: 'department_email',
        fieldLabel: 'Department Email',
        currentValue: 'records@wellstar.org',
        newValue: 'billing@wellstar.org',
        selected: false,
      },
    ],
    status: 'pending',
  },
  {
    id: '3',
    entityName: 'Metro Physical Therapy',
    entityType: 'organization',
    direction: 'push',
    matchConfidence: 87,
    aiReasoning:
      'Contact person changed in Finch. Filevine still has outdated primary contact.',
    timestamp: new Date(Date.now() - 45 * 60 * 1000),
    changes: [
      {
        id: '3a',
        fieldName: 'primary_contact',
        fieldLabel: 'Primary Contact',
        currentValue: 'John Doe',
        newValue: 'Jane Williams',
        selected: true,
      },
      {
        id: '3b',
        fieldName: 'contact_email',
        fieldLabel: 'Contact Email',
        currentValue: 'john.doe@metropt.com',
        newValue: 'jane.williams@metropt.com',
        selected: true,
      },
    ],
    status: 'pending',
  },
  {
    id: '4',
    entityName: 'Dr. Michael Chen',
    entityType: 'provider',
    direction: 'pull',
    matchConfidence: 76,
    aiReasoning:
      'NPI and specialty information differs. Filevine data appears more complete based on recent verification.',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    changes: [
      {
        id: '4a',
        fieldName: 'npi',
        fieldLabel: 'NPI Number',
        currentValue: null,
        newValue: '1234567890',
        selected: true,
      },
      {
        id: '4b',
        fieldName: 'specialty',
        fieldLabel: 'Specialty',
        currentValue: 'General Practice',
        newValue: 'Orthopedic Surgery',
        selected: true,
      },
      {
        id: '4c',
        fieldName: 'license_state',
        fieldLabel: 'License State',
        currentValue: 'GA',
        newValue: 'GA, FL',
        selected: false,
      },
    ],
    status: 'pending',
  },
];

// Utility functions
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

function getConfidenceColor(confidence: number): string {
  if (confidence >= 90) return 'text-emerald-600';
  if (confidence >= 75) return 'text-amber-600';
  return 'text-rose-600';
}

function getConfidenceBg(confidence: number): string {
  if (confidence >= 90) return 'bg-emerald-50 border-emerald-200';
  if (confidence >= 75) return 'bg-amber-50 border-amber-200';
  return 'bg-rose-50 border-rose-200';
}

function getEntityIcon(type: SyncSuggestion['entityType']) {
  switch (type) {
    case 'provider':
      return User;
    case 'organization':
      return Building2;
    case 'contact':
      return User;
    default:
      return User;
  }
}

function getFieldIcon(fieldName: string) {
  if (fieldName.includes('phone') || fieldName.includes('fax')) return Phone;
  if (fieldName.includes('email')) return Mail;
  if (fieldName.includes('address')) return MapPin;
  return FileText;
}

// Individual Sync Card Component
const SyncSuggestionCard = ({
  suggestion,
  onAccept,
  onReject,
  onToggleField,
  index,
}: {
  suggestion: SyncSuggestion;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onToggleField: (suggestionId: string, fieldId: string) => void;
  index: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const EntityIcon = getEntityIcon(suggestion.entityType);
  const isPush = suggestion.direction === 'push';
  const selectedCount = suggestion.changes.filter((c) => c.selected).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className="group relative"
    >
      {/* Card */}
      <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
        {/* Accent line */}
        <div
          className={`absolute top-0 left-0 right-0 h-1 ${isPush ? 'bg-gradient-to-r from-blue-500 to-indigo-500' : 'bg-gradient-to-r from-emerald-500 to-teal-500'}`}
        />

        {/* Header */}
        <div className="p-5 pb-4">
          <div className="flex items-start justify-between gap-4">
            {/* Entity info */}
            <div className="flex items-start gap-3 min-w-0">
              <div
                className={`p-2.5 rounded-xl ${isPush ? 'bg-blue-50' : 'bg-emerald-50'}`}
              >
                {isPush ? (
                  <ArrowUpRight
                    className={`w-5 h-5 ${isPush ? 'text-blue-600' : 'text-emerald-600'}`}
                  />
                ) : (
                  <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-900 truncate">
                    {suggestion.entityName}
                  </h3>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isPush ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}
                  >
                    {isPush ? 'Push to Filevine' : 'Pull from Filevine'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                  <EntityIcon className="w-3.5 h-3.5" />
                  <span className="capitalize">{suggestion.entityType}</span>
                  <span className="text-slate-300">·</span>
                  <Clock className="w-3.5 h-3.5" />
                  <span>{formatRelativeTime(suggestion.timestamp)}</span>
                </div>
              </div>
            </div>

            {/* Confidence badge */}
            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${getConfidenceBg(suggestion.matchConfidence)}`}
            >
              <CheckCircle2
                className={`w-4 h-4 ${getConfidenceColor(suggestion.matchConfidence)}`}
              />
              <span
                className={`text-sm font-semibold ${getConfidenceColor(suggestion.matchConfidence)}`}
              >
                {suggestion.matchConfidence}%
              </span>
            </div>
          </div>

          {/* AI Reasoning */}
          <div className="mt-4 flex items-start gap-2.5 p-3 bg-slate-50 rounded-xl border border-slate-100">
            <Sparkles className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-slate-600 leading-relaxed">
              <span className="font-medium text-slate-700">AI Reasoning: </span>
              {suggestion.aiReasoning}
            </p>
          </div>
        </div>

        {/* Changes section */}
        <div className="border-t border-slate-100">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full px-5 py-3 flex items-center justify-between text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <span>
              {selectedCount} of {suggestion.changes.length} field
              {suggestion.changes.length > 1 ? 's' : ''} selected
            </span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="px-5 pb-4">
                  {/* Field changes table */}
                  <div className="rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 text-left">
                          <th className="w-12 px-4 py-2.5" />
                          <th className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Field
                          </th>
                          <th className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Current
                          </th>
                          <th className="w-10 px-2 py-2.5" />
                          <th className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            New
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {suggestion.changes.map((change) => {
                          const FieldIcon = getFieldIcon(change.fieldName);
                          return (
                            <tr
                              key={change.id}
                              className={`transition-colors ${change.selected ? 'bg-white' : 'bg-slate-50/50'}`}
                            >
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() =>
                                    onToggleField(suggestion.id, change.id)
                                  }
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                    change.selected
                                      ? 'bg-blue-500 border-blue-500'
                                      : 'bg-white border-slate-300 hover:border-slate-400'
                                  }`}
                                >
                                  {change.selected && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <FieldIcon className="w-4 h-4 text-slate-400" />
                                  <span className="font-medium text-slate-700">
                                    {change.fieldLabel}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {change.currentValue ? (
                                  <span
                                    className={`text-sm ${change.selected ? 'text-slate-400 line-through' : 'text-slate-600'}`}
                                  >
                                    {change.currentValue}
                                  </span>
                                ) : (
                                  <span className="text-sm text-slate-300 italic">
                                    Empty
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-3 text-center">
                                <ArrowRight className="w-4 h-4 text-slate-300 inline-block" />
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`text-sm font-medium ${change.selected ? 'text-emerald-600' : 'text-slate-500'}`}
                                >
                                  {change.newValue || (
                                    <span className="text-slate-300 italic font-normal">
                                      Remove
                                    </span>
                                  )}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <button
            type="button"
            onClick={() => onReject(suggestion.id)}
            className="text-sm font-medium text-slate-500 hover:text-rose-600 transition-colors"
          >
            Reject Changes
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Pencil className="w-4 h-4 inline-block mr-1.5" />
              Edit Details
            </button>
            <button
              type="button"
              onClick={() => onAccept(suggestion.id)}
              disabled={selectedCount === 0}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                selectedCount > 0
                  ? isPush
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              <RefreshCw className="w-4 h-4" />
              Sync{' '}
              {selectedCount > 0
                ? `${selectedCount} Field${selectedCount > 1 ? 's' : ''}`
                : 'Fields'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Filter tabs component
const FilterTabs = ({
  activeFilter,
  onFilterChange,
  counts,
}: {
  activeFilter: 'all' | 'push' | 'pull';
  onFilterChange: (filter: 'all' | 'push' | 'pull') => void;
  counts: { all: number; push: number; pull: number };
}) => {
  const tabs = [
    { id: 'all' as const, label: 'All Suggestions', count: counts.all },
    { id: 'push' as const, label: 'Push to Filevine', count: counts.push },
    { id: 'pull' as const, label: 'Pull from Filevine', count: counts.pull },
  ];

  return (
    <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl">
      {tabs.map((tab) => (
        <button
          type="button"
          key={tab.id}
          onClick={() => onFilterChange(tab.id)}
          className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
            activeFilter === tab.id
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          {tab.label}
          {tab.count > 0 && (
            <span
              className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                activeFilter === tab.id
                  ? 'bg-slate-100 text-slate-600'
                  : 'bg-slate-200 text-slate-500'
              }`}
            >
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

// Main component
export const SyncSuggestionCards = () => {
  const [suggestions, setSuggestions] =
    useState<SyncSuggestion[]>(mockSuggestions);
  const [activeFilter, setActiveFilter] = useState<'all' | 'push' | 'pull'>(
    'all',
  );

  const filteredSuggestions = suggestions.filter((s) => {
    if (s.status !== 'pending') return false;
    if (activeFilter === 'all') return true;
    return s.direction === activeFilter;
  });

  const counts = {
    all: suggestions.filter((s) => s.status === 'pending').length,
    push: suggestions.filter(
      (s) => s.status === 'pending' && s.direction === 'push',
    ).length,
    pull: suggestions.filter(
      (s) => s.status === 'pending' && s.direction === 'pull',
    ).length,
  };

  const handleAccept = (id: string) => {
    setSuggestions((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, status: 'accepted' as const } : s,
      ),
    );
  };

  const handleReject = (id: string) => {
    setSuggestions((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, status: 'rejected' as const } : s,
      ),
    );
  };

  const handleToggleField = (suggestionId: string, fieldId: string) => {
    setSuggestions((prev) =>
      prev.map((s) =>
        s.id === suggestionId
          ? {
              ...s,
              changes: s.changes.map((c) =>
                c.id === fieldId ? { ...c, selected: !c.selected } : c,
              ),
            }
          : s,
      ),
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                Sync Suggestions
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Review AI-suggested data updates between Finch and Filevine
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Refresh
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
              >
                Accept All ({counts.all})
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="mb-6">
          <FilterTabs
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            counts={counts}
          />
        </div>

        {/* Cards list */}
        <div className="space-y-4">
          <AnimatePresence mode="popLayout">
            {filteredSuggestions.map((suggestion, index) => (
              <SyncSuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                onAccept={handleAccept}
                onReject={handleReject}
                onToggleField={handleToggleField}
                index={index}
              />
            ))}
          </AnimatePresence>

          {filteredSuggestions.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <CheckCircle2 className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-1">
                All caught up!
              </h3>
              <p className="text-slate-500">
                No pending sync suggestions to review.
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncSuggestionCards;
